import { useState, useRef, useEffect } from "react";
import { Loader2, Sparkles, Pencil, Check, X, CircleAlert, ExternalLink } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { CONTEXT_GUIDE_TARGETS, guideTargetProps } from "@/react/guides";
import { ContextView } from "./wizard/ContextView";
import { useContextPage } from "../context/ContextPageContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { navigateToViewByType } from "@/other/utils/asyncTaskUtils.jsx";
import type { BoardContext } from "@/bryntum-lib/models/BoardContext";
import { OPENAI_KEY_REQUIRED_TITLE, useHasOpenAIKey } from "@/react/hooks/useHasOpenAIKey";
import { contextDisplayName } from "../utils/contextDisplayName";
import { ContextWizardStateBanner } from "./ContextWizardStateBanner";
import { useHasNonPreviewGroups } from "../context/useContextViewState";

const ContextTitleArea = ({
  contextRecord,
  hasOpenAIKey,
}: {
  contextRecord: BoardContext;
  hasOpenAIKey: boolean;
}) => {
  const { renameContext } = useContextPage();
  const isLocked = contextRecord.isActive === true;
  const displayName = contextDisplayName(contextRecord.name);

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(displayName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setValue(displayName);
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [isEditing, displayName]);

  const handleSave = async () => {
    const trimmed = value.trim();
    setIsEditing(false);
    if (trimmed && trimmed !== displayName) {
      await renameContext(contextRecord.id, trimmed);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setValue(displayName);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") handleCancel();
          }}
          className="flex-1 min-w-0 rounded-md border border-primary/50 bg-background px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30"
          autoFocus
        />
        <button
          type="button"
          onClick={handleSave}
          className="flex h-6 w-6 items-center justify-center rounded text-primary hover:bg-primary/10 transition-colors shrink-0"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted transition-colors shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-0 flex-1 group/title">
      <span className="text-sm font-semibold text-foreground truncate">{displayName}</span>
      {!isLocked && hasOpenAIKey && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/40 hover:text-foreground hover:bg-muted opacity-0 group-hover/title:opacity-100 transition-all shrink-0"
          title="Rename"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

interface ContextHeaderBarProps {
  contextRecord: BoardContext;
  hasOpenAIKey: boolean;
}

const ContextHeaderBar = ({
  contextRecord,
  hasOpenAIKey,
}: ContextHeaderBarProps) => {
  return (
    <div className="shrink-0 flex items-center gap-3 px-4 h-12 border-b border-border/40">
      <ContextTitleArea contextRecord={contextRecord} hasOpenAIKey={hasOpenAIKey} />
    </div>
  );
};

interface AddContextButtonProps {
  onAddContext: () => void;
  isDuplicating: boolean;
  hasOpenAIKey: boolean;
}

const AddContextButton = ({
  onAddContext,
  isDuplicating,
  hasOpenAIKey,
}: AddContextButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span tabIndex={!hasOpenAIKey ? 0 : undefined}>
        <button
          type="button"
          onClick={onAddContext}
          disabled={!hasOpenAIKey || isDuplicating}
          {...guideTargetProps(CONTEXT_GUIDE_TARGETS.addContext)}
          className={cn(
            "relative inline-flex items-center gap-1.5 h-8 px-4 rounded-md text-xs font-semibold shrink-0",
            "bg-gradient-to-r from-primary/90 to-primary text-primary-foreground",
            "shadow-[0_0_12px_2px_hsl(var(--primary)/0.25)]",
            "hover:shadow-[0_0_18px_4px_hsl(var(--primary)/0.35)] hover:brightness-110",
            "active:scale-[0.97] transition-all duration-150",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none disabled:shadow-none",
          )}
        >
          {isDuplicating ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5 shrink-0" />
          )}
          {isDuplicating ? "Duplicating…" : "Add Context"}
        </button>
      </span>
    </TooltipTrigger>
    {!hasOpenAIKey && (
      <TooltipContent side="top" className="text-xs max-w-[220px] text-center">
        {OPENAI_KEY_REQUIRED_TITLE}
      </TooltipContent>
    )}
  </Tooltip>
);

interface ContextMakeActiveFooterProps {
  onMakeActive: () => void;
  onNavigateToManifest: () => void;
  onAddContext: () => void;
  isMakingActive: boolean;
  isDuplicating: boolean;
  canMakeActive: boolean;
  hasOpenAIKey: boolean;
  disabledReason?: string;
}

const ContextMakeActiveFooter = ({
  onMakeActive,
  onNavigateToManifest,
  onAddContext,
  isMakingActive,
  isDuplicating,
  canMakeActive,
  hasOpenAIKey,
  disabledReason,
}: ContextMakeActiveFooterProps) => {
  const isEnabled = canMakeActive && hasOpenAIKey;

  const actionButton = isEnabled ? (
    <Button
      size="sm"
      onClick={onMakeActive}
      disabled={isMakingActive}
      {...guideTargetProps(CONTEXT_GUIDE_TARGETS.contextFooter)}
      className="h-8 gap-1.5 px-4 text-xs font-semibold shadow-sm"
    >
      {isMakingActive && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {isMakingActive ? "Making Active Context…" : "Make Active Context"}
    </Button>
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Button
            size="sm"
            disabled
            {...guideTargetProps(CONTEXT_GUIDE_TARGETS.contextFooter)}
            className="h-8 px-4 text-xs opacity-40 cursor-not-allowed"
          >
            Make Active Context
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-center text-xs">
        {disabledReason}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <div className="shrink-0 border-t border-border/40 bg-muted/25 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <CircleAlert className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Setting this Context to Active will regenerate the board&apos;s{" "}
            <button
              type="button"
              onClick={onNavigateToManifest}
              className="font-medium text-primary inline-flex items-center gap-0.5 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm"
            >
              Marketing Manifest
              <ExternalLink className="h-2.5 w-2.5 shrink-0" aria-hidden />
            </button>
            {" "}and marketing tags. This may take several minutes, and it cannot be undone.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <AddContextButton
            onAddContext={onAddContext}
            isDuplicating={isDuplicating}
            hasOpenAIKey={hasOpenAIKey}
          />
          {actionButton}
        </div>
      </div>
    </div>
  );
};

const ContextActiveManifestFooter = ({
  onNavigateToManifest,
  onAddContext,
  isDuplicating,
  hasOpenAIKey,
}: {
  onNavigateToManifest: () => void;
  onAddContext: () => void;
  isDuplicating: boolean;
  hasOpenAIKey: boolean;
}) => (
  <div className="shrink-0 border-t border-border/40 bg-muted/25 px-4 py-3">
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <p className="min-w-0 flex-1 text-xs text-muted-foreground leading-relaxed">
        This is the active board context. Its Marketing Manifest and tags were generated automatically.
      </p>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onNavigateToManifest}
          {...guideTargetProps(CONTEXT_GUIDE_TARGETS.contextFooter)}
          className="h-8 shrink-0 gap-1.5 px-4 text-xs font-semibold"
        >
          View Marketing Manifest
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </Button>
        <AddContextButton
          onAddContext={onAddContext}
          isDuplicating={isDuplicating}
          hasOpenAIKey={hasOpenAIKey}
        />
      </div>
    </div>
  </div>
);

interface ContextDetailProps {
  contextRecord: BoardContext;
  showWizardBanner?: boolean;
}

export const ContextDetail = ({
  contextRecord,
  showWizardBanner = false,
}: ContextDetailProps) => {
  const {
    makeActive,
    isMakingActive,
    duplicateContext,
    isDuplicating,
    openAddContextExtend,
    openModalForContext,
  } = useContextPage();
  const { viewManager } = useSwivuBryntumContext() as any;
  const hasOpenAIKey = useHasOpenAIKey();
  const hasGroups = useHasNonPreviewGroups(contextRecord.id);

  const navigateToManifest = () => {
    navigateToViewByType(viewManager, VIEW_TYPES.marketingManifest, "Marketing Manifest", {});
  };

  const record = contextRecord;
  const isCompleted = record.status === "completed";
  const showMakeActive = !!record && !record.isActive && isCompleted;
  const showActiveManifestFooter = !!record && record.isActive === true;
  const canMakeActive = isCompleted && hasGroups;
  const disabledReason = !hasOpenAIKey
    ? OPENAI_KEY_REQUIRED_TITLE
    : "Generate context first before activating.";

  const handleAddContext = () => void openAddContextExtend(record.id);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ContextHeaderBar
        contextRecord={record}
        hasOpenAIKey={hasOpenAIKey}
      />

      {showWizardBanner && (
        <ContextWizardStateBanner
          contextId={record.id}
          onContinueInDialog={() => openModalForContext(record.id, "resume")}
        />
      )}

      <div
        className="flex-1 min-h-0 overflow-hidden flex flex-col"
        {...guideTargetProps(CONTEXT_GUIDE_TARGETS.contextDetail)}
      >
        <ContextView
          onDuplicateContext={() => duplicateContext(record.id)}
          isDuplicatingContext={isDuplicating}
        />
      </div>

      {showMakeActive && (
        <ContextMakeActiveFooter
          onMakeActive={() => makeActive(record.id)}
          onNavigateToManifest={navigateToManifest}
          onAddContext={handleAddContext}
          isMakingActive={isMakingActive}
          isDuplicating={isDuplicating}
          canMakeActive={canMakeActive}
          hasOpenAIKey={hasOpenAIKey}
          disabledReason={disabledReason}
        />
      )}

      {showActiveManifestFooter && (
        <ContextActiveManifestFooter
          onNavigateToManifest={navigateToManifest}
          onAddContext={handleAddContext}
          isDuplicating={isDuplicating}
          hasOpenAIKey={hasOpenAIKey}
        />
      )}
    </div>
  );
};
